// src/components/billing/PaymentStatusChart.jsx
import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const PaymentStatusChart = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex justify-center items-center h-full text-gray-500">
        <div className="text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="mt-2">No payment status data available</p>
        </div>
      </div>
    );
  }

  // Define colors for each status
  const statusColors = {
    paid: '#10B981',      // Green
    pending: '#F59E0B',   // Amber
    overdue: '#EF4444',   // Red
    draft: '#6B7280',     // Gray
    cancelled: '#9CA3AF', // Light Gray
    sent: '#3B82F6'       // Blue
  };

  // Create chart data from the status breakdown
  const chartData = [
    { name: 'Paid', value: data.paid || 0, color: statusColors.paid },
    { name: 'Pending', value: data.pending || 0, color: statusColors.pending },
    { name: 'Overdue', value: data.overdue || 0, color: statusColors.overdue },
    { name: 'Draft', value: data.draft || 0, color: statusColors.draft },
    { name: 'Sent', value: data.sent || 0, color: statusColors.sent },
    { name: 'Cancelled', value: data.cancelled || 0, color: statusColors.cancelled }
  ].filter(item => item.value > 0); // Only show statuses with data

  // If no data, show empty state
  if (chartData.length === 0) {
    return (
      <div className="flex justify-center items-center h-full text-gray-500">
        <div className="text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="mt-2">No invoices in selected period</p>
        </div>
      </div>
    );
  }

  const totalInvoices = chartData.reduce((sum, item) => sum + item.value, 0);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const percentage = ((data.value / totalInvoices) * 100).toFixed(1);
      
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <div className="flex items-center mb-1">
            <div 
              className="w-3 h-3 rounded-full mr-2" 
              style={{ backgroundColor: data.payload.color }}
            ></div>
            <p className="text-sm font-medium text-gray-900">{data.name}</p>
          </div>
          <p className="text-sm text-gray-700">
            {data.value} invoice{data.value !== 1 ? 's' : ''}
          </p>
          <p className="text-xs text-gray-500">
            {percentage}% of total
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = ({ payload }) => {
    return (
      <div className="flex flex-wrap justify-center gap-4 mt-4">
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center">
            <div 
              className="w-3 h-3 rounded-full mr-2" 
              style={{ backgroundColor: entry.color }}
            ></div>
            <span className="text-sm text-gray-700">
              {entry.value} ({chartData.find(item => item.name === entry.value)?.value || 0})
            </span>
          </div>
        ))}
      </div>
    );
  };

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({
    cx, cy, midAngle, innerRadius, outerRadius, percent
  }) => {
    if (percent < 0.05) return null; // Don't show label if less than 5%
    
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize="12"
        fontWeight="600"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="45%"
          labelLine={false}
          label={renderCustomizedLabel}
          outerRadius={80}
          innerRadius={30}
          paddingAngle={2}
          dataKey="value"
        >
          {chartData.map((entry, index) => (
            <Cell 
              key={`cell-${index}`} 
              fill={entry.color}
              stroke={entry.color}
              strokeWidth={1}
            />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
        <Legend 
          content={<CustomLegend />}
          verticalAlign="bottom"
          height={60}
        />
      </PieChart>
    </ResponsiveContainer>
  );
};

export default PaymentStatusChart;